import React, { useState, useEffect, useMemo, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import BugReportIcon from "@material-ui/icons/BugReport";

const APPBAR_HEIGHT = 64;
const APPBAR_HEIGHT_MOBILE = 56;

const useStyles = makeStyles((theme) => ({
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  toolbar: {
    minHeight: APPBAR_HEIGHT,
    [theme.breakpoints.down("sm")]: {
      minHeight: APPBAR_HEIGHT_MOBILE,
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },
  menuButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      marginRight: theme.spacing(1),
    },
  },
  toolbarLeft: {
    display: "flex",
    alignItems: "center",
  },
  toolbarCenter: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    marginLeft: theme.spacing(2),
  },
  toolbarRight: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(0.5),
    },
  },
  greeting: {
    opacity: 0.9,
    fontSize: "0.875rem",
  },
  dateTime: {
    opacity: 0.7,
    fontSize: "0.75rem",
  },
  sidebarToggle: {
    fontSize: "0.8rem",
  },
}));

const AppToolbar = ({
  open,
  setOpen,
  alwaysOpen,
  onSidebarToggle,
  colorMode,
  onColorModeToggle,
  onBugReport,
  closeSubDrawers,
  userInfo,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 10000);
    return () => clearInterval(timer);
  }, []);

  const timeDisplay = useMemo(() => {
    const locale = "en";
    const day = currentTime.toLocaleDateString(locale, { weekday: "long" });
    const month = currentTime.toLocaleDateString(locale, { month: "long" });
    const date = currentTime.getDate();
    const time = currentTime.toLocaleTimeString(locale, {
      hour: "numeric",
      hour12: true,
      minute: "numeric",
    });
    const hour = currentTime.getHours();
    const greeting = hour < 12 ? "Morning" : hour < 17 ? "Afternoon" : "Evening";
    const firstName = userInfo?.firstName || "";

    return {
      time,
      date: `${day}, ${month} ${date}`,
      greeting: `Good ${greeting}${firstName ? ` ${firstName}` : ""}!`,
    };
  }, [currentTime, userInfo?.firstName]);

  const handleMenuClick = useCallback(() => {
    if (open) {
      setOpen(false);
      closeSubDrawers();
    } else {
      setOpen(true);
    }
  }, [open, setOpen, closeSubDrawers]);

  return (
    <AppBar position="fixed" className={classes.appBar}>
      <Toolbar className={classes.toolbar}>
        <div className={classes.toolbarLeft}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleMenuClick}
            className={classes.menuButton}
          >
            {open ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
        </div>

        {!smallScreen && (
          <div className={classes.toolbarCenter}>
            <Typography variant="h6" noWrap>
              Gee Tee Dashboard
              {process.env.NODE_ENV === "development" && " (DEV)"}
            </Typography>
            <Typography className={classes.dateTime}>
              {timeDisplay.time} &bull; {timeDisplay.date}
            </Typography>
          </div>
        )}

        {smallScreen && (
          <Typography variant="body1" style={{ flex: 1, marginLeft: 8 }}>
            Gee Tee {process.env.NODE_ENV === "development" && "(DEV)"}
          </Typography>
        )}

        <div className={classes.toolbarRight}>
          {!smallScreen && (
            <>
              <Typography className={classes.greeting}>{timeDisplay.greeting}</Typography>
              <Tooltip title="Report a problem">
                <IconButton color="inherit" onClick={onBugReport}>
                  <BugReportIcon />
                </IconButton>
              </Tooltip>
            </>
          )}

          <Tooltip title={colorMode === "dark" ? "Light mode" : "Dark mode"}>
            <IconButton color="inherit" onClick={onColorModeToggle}>
              {colorMode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Tooltip>

          {!smallScreen && (
            <FormControlLabel
              control={
                <Switch
                  color="secondary"
                  checked={alwaysOpen}
                  onChange={onSidebarToggle}
                  size="small"
                />
              }
              label={
                <Typography className={classes.sidebarToggle}>
                  {alwaysOpen ? "Opened" : "Closed"}
                </Typography>
              }
            />
          )}
        </div>
      </Toolbar>
    </AppBar>
  );
};

export default AppToolbar;
